import type { LucideIcon } from 'lucide-react';
import { Shield, Zap, Unlock, Eye } from 'lucide-react';
import { useT } from '@/i18n/provider';
import { LocalizedAnchor } from './LocalizedLink';

interface Pillar {
  key: string;
  icon: LucideIcon;
}

const pillars: Pillar[] = [
  { key: 'eu', icon: Shield },
  { key: 'speed', icon: Zap },
  { key: 'openness', icon: Unlock },
  { key: 'aiControl', icon: Eye },
];

export function SocialProofSection() {
  const t = useT();

  return (
    <section className="py-20 sm:py-28 bg-gray-50 dark:bg-gray-950 border-y border-gray-200 dark:border-gray-800/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl mb-14 sm:mb-16 animate-on-scroll">
          <p className="eyebrow">{t('socialProof.eyebrow')}</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-950 dark:text-white text-balance">
            {t('socialProof.title')}
          </h2>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 text-balance">
            {t('socialProof.subtitle')}
          </p>
        </div>

        {/* Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-10 animate-on-scroll" data-animate="fade-up">
          {pillars.map((pillar) => {
            const Icon = pillar.icon;
            return (
              <div key={pillar.key}>
                <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                <div className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
                  {t(`socialProof.${pillar.key}.title`)}
                </div>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t(`socialProof.${pillar.key}.description`)}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA link */}
        <div className="mt-14 animate-on-scroll" data-animate="fade-up">
          <LocalizedAnchor
            href="/contact"
            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors"
          >
            {t('socialProof.cta')}
            <span aria-hidden="true"> &rarr;</span>
          </LocalizedAnchor>
        </div>
      </div>
    </section>
  );
}
